import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SexyModule } from '@qid/sexy';

interface CalendarDay {
  date: number | null;
  fullDate: Date | null;
  enabled: boolean;
}

@Component({
  selector: 'date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SexyModule
  ],
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  @Input() show: boolean = false;
  @Input() maxDate: Date = new Date();
  @Input() mode: 'mobile' | 'desktop' = 'desktop'; // Controls single vs dual calendar

  @Output() showChange = new EventEmitter<boolean>();
  @Output() dateRangeSelected = new EventEmitter<{ start: Date, end: Date }>();
  @Output() closed = new EventEmitter<void>();

  // Calendar data
  public weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  public currentMonthDays: CalendarDay[] = [];
  public previousMonthDays: CalendarDay[] = [];
  public currentMonth: Date = new Date();
  public hoveredDate: Date | null = null;
  public startDate: Date | null = null;
  public endDate: Date | null = null;

  constructor(private el: ElementRef<HTMLElement>) {
  }

  ngOnInit() {
    // Portal to body so position:fixed overlay centers on the viewport
    // even when hosted inside an ion-modal (transformed ancestors trap fixed elements)
    document.body.appendChild(this.el.nativeElement);
    this.updateCalendarDays();
  }

  ngOnDestroy() {
    this.el.nativeElement.remove();
  }

  public closeModal() {
    this.show = false;
    this.showChange.emit(false);
    this.closed.emit();
  }

  public updateCalendarDays() {
    if (this.mode === 'desktop') {
      const prevMonth = new Date(this.currentMonth);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      this.previousMonthDays = this.generateMonthDays(prevMonth);
    }
    this.currentMonthDays = this.generateMonthDays(this.currentMonth);
  }

  private generateMonthDays(date: Date): CalendarDay[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: CalendarDay[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, fullDate: null, enabled: false });
    }

    // Add actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const fullDate = new Date(year, month, day);
      const isFuture = fullDate > this.maxDate;
      days.push({
        date: day,
        fullDate: fullDate,
        enabled: !isFuture
      });
    }

    return days;
  }

  public selectDate(day: CalendarDay) {
    if (!day.enabled || !day.fullDate) return;

    const selectedDate = new Date(day.fullDate);

    if (!this.startDate || (this.startDate && this.endDate)) {
      // Start new selection
      this.startDate = selectedDate;
      this.endDate = null;
    } else {
      // Complete the range
      if (selectedDate < this.startDate) {
        // If selected date is before start date, make it the new start date
        this.startDate = selectedDate;
        this.endDate = null;
      } else {
        this.endDate = selectedDate;
      }
    }
  }

  public hoverDate(day: CalendarDay) {
    if (!day.enabled || !day.fullDate) return;
    this.hoveredDate = day.fullDate;
  }

  public clearHover() {
    this.hoveredDate = null;
  }

  public isSelected(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    const date = day.fullDate;
    return (this.startDate && this.isSameDay(date, this.startDate)) ||
      (this.endDate && this.isSameDay(date, this.endDate));
  }

  public isRangeStart(day: CalendarDay): boolean {
    if (!day.fullDate || !this.startDate) return false;
    return this.isSameDay(day.fullDate, this.startDate);
  }

  public shouldShowStartBackground(day: CalendarDay): boolean {
    if (!this.isRangeStart(day)) return false;

    // Show background if there's an actual end date
    if (this.endDate) return true;

    // In desktop mode, show background if hovering over a date AFTER the start date
    if (this.mode === 'desktop' && this.hoveredDate && this.startDate) {
      return this.hoveredDate > this.startDate;
    }

    // In mobile mode, don't show background until end date is selected
    return false;
  }

  public isRangeEnd(day: CalendarDay): boolean {
    if (!day.fullDate) return false;

    // If end date is set, check if this is the actual end date
    if (this.endDate) {
      return this.isSameDay(day.fullDate, this.endDate);
    }

    // If only start date is set and in desktop mode, show hovered date as temporary end
    if (this.mode === 'desktop' && this.startDate && !this.endDate && this.hoveredDate) {
      return day.fullDate > this.startDate && this.isSameDay(day.fullDate, this.hoveredDate);
    }

    return false;
  }

  public isHoveredEnd(day: CalendarDay): boolean {
    if (!day.fullDate || this.mode === 'mobile') return false;

    // Show as hovered end only if start is selected, end is not, this is the hovered date, AND it's after the start date
    if (this.startDate && !this.endDate && this.hoveredDate) {
      return day.fullDate > this.startDate && this.isSameDay(day.fullDate, this.hoveredDate);
    }

    return false;
  }

  public isInRange(day: CalendarDay): boolean {
    if (!day.fullDate || !this.startDate) return false;

    const date = day.fullDate;
    const start = this.startDate;
    const end = this.endDate || (this.mode === 'desktop' && this.hoveredDate && this.hoveredDate > this.startDate ? this.hoveredDate : null);

    if (!end) return false;

    return date > start && date < end;
  }

  public isToday(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    return this.isSameDay(day.fullDate, new Date());
  }

  public isFirstDayOfMonth(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    return day.fullDate.getDate() === 1;
  }

  public isLastDayOfMonth(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    const nextDay = new Date(day.fullDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.getDate() === 1;
  }

  public isSunday(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    return day.fullDate.getDay() === 0;
  }

  public isSaturday(day: CalendarDay): boolean {
    if (!day.fullDate) return false;
    return day.fullDate.getDay() === 6;
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  }

  public previousMonth() {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.updateCalendarDays();
  }

  public nextMonth() {
    if (!this.canGoToNextMonth()) return;
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.updateCalendarDays();
  }

  public canGoToNextMonth(): boolean {
    const nextMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    const today = new Date();
    // Allow navigation only if next month is not in the future
    return nextMonth.getFullYear() < today.getFullYear() ||
      (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() <= today.getMonth());
  }

  public getPreviousMonthLabel(): string {
    const prevMonth = new Date(this.currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    return prevMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  public getCurrentMonthLabel(): string {
    return this.currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  public clearDates() {
    this.startDate = null;
    this.endDate = null;
    this.hoveredDate = null;
  }

  public applyDates() {
    if (!this.startDate || !this.endDate) return;

    // Set start date to beginning of day
    const start = new Date(this.startDate);
    start.setHours(0, 0, 0, 0);

    // Set end date to end of day
    const end = new Date(this.endDate);
    end.setHours(23, 59, 59, 999);

    this.dateRangeSelected.emit({ start, end });
    this.closeModal();
  }

  public get isValidRange(): boolean {
    return this.startDate !== null && this.endDate !== null;
  }
}
